import { useLayoutEffect, useRef, useState } from 'react'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import { Box, ButtonBase, Grid, IconButton, Typography, useTheme } from '@mui/material'

import useStyles from './index.styles'
import { ILocationFolder } from './types'

interface IProps {
  navigationTrail: ILocationFolder[]
  onHome: () => void
  onSelect: (index: number) => void
}

const Breadcrumbs = ({ navigationTrail, onHome, onSelect }: IProps) => {
  const classes = useStyles
  const theme = useTheme()
  const [collapseBreadcrumbAncestors, setCollapseBreadcrumbAncestors] = useState<boolean>(false)
  const breadcrumbsRef = useRef<HTMLDivElement | null>(null)
  const homeBreadcrumbRef = useRef<HTMLButtonElement | null>(null)
  const breadcrumbMeasureRefs = useRef<(HTMLSpanElement | null)[]>([])

  // Decides whether the breadcrumbs fit on one line. If the full trail (with every folder name in full) is wider than the
  // container, all ancestors collapse to "…" so the last item keeps its full name. CSS flex-shrink can't express "shrink
  // everything else before this one", hence the manual measurement. Runs before paint to avoid a flash of overflowing text.
  useLayoutEffect(() => {
    // With 0 or 1 items there are no ancestors to collapse. Each branch returns a cleanup so the effect returns consistently.
    if (navigationTrail.length < 2) {
      setCollapseBreadcrumbAncestors(false)
      return () => {}
    }

    const breadcrumbContainer = breadcrumbsRef.current
    if (!breadcrumbContainer) return () => {}

    const measureOverflow = () => {
      // The container has no width yet (e.g. the modal is still opening), so there is nothing meaningful to measure.
      // Collapse so the trail never starts out too wide; the observer below re-measures once it has a real width.
      if (breadcrumbContainer.clientWidth === 0) {
        setCollapseBreadcrumbAncestors(true)
        return
      }

      // Natural width of each folder name. These come from hidden, unconstrained copies of the labels because the visible
      // labels are clipped by their container and would always report a width that fits.
      const labelWidth = breadcrumbMeasureRefs.current.reduce((total, element) => total + (element?.getBoundingClientRect().width ?? 0), 0)

      // Width taken by every "/" between items
      const separatorWidth = Array.from(breadcrumbContainer.querySelectorAll('[data-breadcrumb-separator]')).reduce(
        (total, element) => total + element.getBoundingClientRect().width,
        0
      )

      // Width taken by the home icon button that leads the trail
      const homeWidth = homeBreadcrumbRef.current?.getBoundingClientRect().width ?? 0

      // Space the row can actually use
      const availableWidth = breadcrumbContainer.clientWidth

      // Needed width = home + separators + full-length names. If it exceeds the space available, collapse the ancestors.
      setCollapseBreadcrumbAncestors(labelWidth + separatorWidth + homeWidth > availableWidth)
    }

    measureOverflow()

    // Re-measure when the container resizes (viewport/modal changes) or a label's size changes (e.g. the font finishes loading)
    const resizeObserver = new ResizeObserver(measureOverflow)
    resizeObserver.observe(breadcrumbContainer)
    breadcrumbMeasureRefs.current.forEach((element) => {
      if (element) resizeObserver.observe(element)
    })

    return () => resizeObserver.disconnect()
  }, [navigationTrail])

  return (
    <Grid container={true} sx={classes.breadcrumbs} data-testid="location-breadcrumbs" ref={breadcrumbsRef}>
      <IconButton ref={homeBreadcrumbRef} aria-label="all locations" size="small" onClick={onHome} sx={classes.homeButton}>
        <HomeOutlinedIcon sx={classes.homeIcon} />
      </IconButton>
      {navigationTrail.map((folder, index) => (
        <Grid key={folder.id} container={true} sx={classes.breadcrumbItem(index === navigationTrail.length - 1)}>
          <Typography aria-hidden="true" data-breadcrumb-separator="" sx={classes.breadcrumbSeparator}>
            /
          </Typography>
          <ButtonBase
            aria-label={`Go to ${folder.name}`}
            aria-current={index === navigationTrail.length - 1 ? 'page' : undefined}
            onClick={() => onSelect(index)}
            sx={classes.breadcrumbLink}
          >
            <Typography component="span" variant="overline" sx={classes.breadcrumbText}>
              {collapseBreadcrumbAncestors && index < navigationTrail.length - 1 ? '…' : folder.name}
            </Typography>
          </ButtonBase>
        </Grid>
      ))}
      <Box aria-hidden="true" sx={classes.breadcrumbMeasureContainer}>
        {navigationTrail.map((folder, index) => (
          <Typography
            key={folder.id}
            component="span"
            variant="overline"
            data-breadcrumb-measure=""
            ref={(element: HTMLSpanElement | null) => {
              breadcrumbMeasureRefs.current[index] = element
            }}
            sx={[classes.breadcrumbText(theme), classes.breadcrumbMeasure]}
          >
            {folder.name}
          </Typography>
        ))}
      </Box>
    </Grid>
  )
}

export default Breadcrumbs
